/**
 * Run non-critical work shortly after first user input.
 * Always falls back to a long timeout so work still runs without interaction.
 *
 * Deliberately avoids requestIdleCallback: browser "idle" can happen inside
 * Lighthouse's initial-load window and allow heavy optional work to delay LCP.
 */
export function scheduleDeferredWork(
    work: () => void,
    fallbackMs: number = 15_000,
    interactionDelayMs: number = 2000,
): () => void {
    let fallbackTimerId: number | undefined;
    let interactionTimerId: number | undefined;
    let done = false;

    const run = () => {
        if (done) return;
        done = true;
        cleanup();
        work();
    };

    const cleanup = () => {
        if (typeof fallbackTimerId === 'number') {
            window.clearTimeout(fallbackTimerId);
        }
        if (typeof interactionTimerId === 'number') {
            window.clearTimeout(interactionTimerId);
        }
        window.removeEventListener('pointerdown', onFirstInteraction);
        window.removeEventListener('keydown', onFirstInteraction);
        window.removeEventListener('touchstart', onFirstInteraction);
    };

    if (typeof window === 'undefined') {
        return () => undefined;
    }

    let interactionSeen = false;
    function onFirstInteraction() {
        if (interactionSeen) return;
        interactionSeen = true;
        interactionTimerId = window.setTimeout(run, interactionDelayMs);
    }

    window.addEventListener('pointerdown', onFirstInteraction, { once: true, passive: true });
    window.addEventListener('keydown', onFirstInteraction, { once: true, passive: true });
    window.addEventListener('touchstart', onFirstInteraction, { once: true, passive: true });
    fallbackTimerId = window.setTimeout(run, fallbackMs);

    return cleanup;
}
